// SelectInput.jsx
import { useField, useFormikContext } from 'formik'
import React, { CSSProperties, useEffect, useMemo } from 'react'
import ReactSelect, {
  GroupBase,
  OptionsOrGroups,
  StylesConfig
} from 'react-select'
import CreatableSelect from 'react-select/creatable'
import AsyncSelect from 'react-select/async'
import AsyncCreatableSelect from 'react-select/async-creatable'
import { useQuery } from '@tanstack/react-query'
import { getAddress, getAddresses } from '../address'
import { useTranslation } from 'react-i18next'
import {
  CFieldProps,
  GroupedOption,
  OptionType,
  RequestParams,
  get_request_api
} from '@velocity/shared'
import chroma from 'chroma-js'

type InputProps = CFieldProps & {
  label?: string
  disabled?: boolean
  isMulti?: boolean
  options?: OptionsOrGroups<any, GroupBase<any>>
  isLoading?: boolean
  validator?: any
  placeholder?: string
  loadOptions?: any
  defaultOptions?: any
  hideIndicator?: boolean
}
export const CSelect = ({
  validator,
  hideIndicator,
  className,
  required,
  ...props
}: InputProps) => {
  const { t } = useTranslation()
  const formik = useFormikContext<any>()
  const [createVal, setCreateVal] = React.useState<string>('')
  const [, meta, helpers] = useField(props.name)
  const [pageParams, setPageParams] =
    React.useState<
      [string, (RequestParams | undefined)?, ('list' | 'detail' | undefined)?]
    >()
  useEffect(
    () =>
      props.optionsQuery?.queryKey ?
        setPageParams(props.optionsQuery?.queryKey)
      : undefined,
    [props.optionsQuery]
  )
  const updatePageParams = (key: string, value: any) => {
    if (pageParams)
      setPageParams((prev) => {
        if (prev) {
          const val = { ...(prev[1] || {}), [key]: value }
          return [prev[0], val, prev[2]]
        }
      })
  }
  const { data, isFetching }: any = useQuery({
    queryKey: pageParams!,
    queryFn: props.selectProps?.requestFn || get_request_api,
    enabled: props.optionsQuery?.enabled || false
  })
  const options = useMemo(() => {
    let opts: any = props.options || []
    if (!props.options && data?.rows)
      opts = data.rows.map((item: any) => ({
        label:
          props.optionsQuery?.name ?
            item[props.optionsQuery?.name] || item.name
          : item.name,
        value: props.optionsQuery?.id ? item[props.optionsQuery?.id] : item.id,
        data: item
      }))
    if (props.selectProps?.extraOptions) {
      opts = props.selectProps.extraOptions.concat(opts)
    }
    if (props.selectProps?.translateOptions) {
      opts.forEach((option: any) => {
        option.label = option.label ? t(option.label) : option.label
      })
    }
    return opts
    // eslint-disable-next-line
  }, [data, props.options])
  const value = useMemo(() => {
    const v = createVal || meta.value
    const opns =
      props.grouped ? options.flatMap((o: any) => o.options) : options
    return (
      v?.toString()?.length ?
        props.isMulti ?
          (v as any)?.map(
            (val: any) =>
              opns?.find((o: any) => o.value === val) || {
                value: val,
                label: props.useValueAsLabel ? val : ''
              }
          )
        : {
            value: v,
            label:
              opns?.find((o: any) => o.value === v)?.label ||
              props.selectProps?.fallBackLabel ||
              (props.useValueAsLabel ? v : '')
          }
      : null
    )
    // eslint-disable-next-line
  }, [meta.value, props.isMulti, createVal, options])
  const groupStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  }
  const groupBadgeStyles: CSSProperties = {
    backgroundColor: '#EBECF0',
    borderRadius: '2em',
    color: '#172B4D',
    display: 'inline-block',
    fontSize: 12,
    fontWeight: 'normal',
    lineHeight: '1',
    minWidth: 1,
    padding: '0.16666666666667em 0.5em',
    textAlign: 'center'
  }

  const formatGroupLabel = (data: GroupedOption) => (
    <div style={groupStyles}>
      <span>{data.label}</span>
      {!data.noCount && (
        <span style={groupBadgeStyles}>{data.options.length}</span>
      )}
    </div>
  )
  const onChange = (val: any) => {
    setCreateVal('')
    if (props.type === 'address') {
      getAddress(props.name, val, formik, props.addressProps?.fields)
    } else if (props.createFn && val.__isNew__) {
      props.createFn(val)
      setCreateVal(val.value)
    } else {
      if (props.selectFn) props.selectFn(val, props.name)
      if (props.isMulti) {
        helpers.setValue(val.map((v: any) => v.value))
      } else {
        helpers.setValue(val.value)
      }
    }
  }
  const onInputChange = (inputValue: string) => {
    props.searchFn?.(inputValue)
    if (props.optionsQuery?.search) updatePageParams('search', inputValue)
  }
  const handleMenuOpen = () => {
    requestAnimationFrame(() => {
      const menu = document.querySelector('.react-select__menu-list')
      const selected = menu?.querySelector('.react-select__option--is-selected')
      selected?.scrollIntoView({ block: 'nearest' })
    })
  }
  const commonProps: any = useMemo(
    () => ({
      ...props,
      components:
        hideIndicator ?
          {
            DropdownIndicator: () => null,
            IndicatorSeparator: () => null
          }
        : {},
      styles: {
        control: (base: any) => ({
          ...base,
          borderColor:
            meta?.touched && meta?.error ? '#ef4444' : base.borderColor,
          '&:hover': {
            borderColor:
              meta?.touched && meta?.error ? '#ef4444' : base.borderColor
          }
        }),
        placeholder: (base: any) => ({
          ...base,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap', // truncate long placeholder
          maxWidth: 'calc(100% - 20px)',
          ...(props.name === 'color' ? dot('#ccc') : {})
        }),
        menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
        ...(props.name === 'color' ? colourStyles : {})
      },
      isDisabled: props.disabled,
      menuPortalTarget: document.body,
      className: 'basic-multi-select',
      classNamePrefix: 'react-select',
      onChange,
      isLoading: isFetching || props.isLoading,
      onInputChange,
      options,
      loadOptions: props.type === 'address' ? getAddresses : props.loadOptions,
      value,
      menuShouldScrollIntoView: true,
      cacheOptions: true,
      closeMenuOnSelect: !props.isMulti,
      onMenuOpen: handleMenuOpen
      // menuPlacement:"auto"
    }),
    // eslint-disable-next-line
    [value, options, isFetching, props.isLoading]
  )
  if (props.grouped) commonProps['formatGroupLabel'] = formatGroupLabel
  return (
    <div
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {(props.createFn || props.createAble) && props.loadOptions ?
        <AsyncCreatableSelect {...commonProps} defaultOptions />
      : props.createFn || props.createAble ?
        <CreatableSelect
          {...commonProps}
          isValidNewOption={(inputValue: any) =>
            validator ? validator(inputValue) : true
          }
        />
      : props.loadOptions || props.type === 'address' ?
        <AsyncSelect {...commonProps} defaultOptions />
      : <ReactSelect {...commonProps} />}
    </div>
  )
}

const dot = (color = 'transparent') => ({
  alignItems: 'center',
  display: 'flex',

  ':before': {
    backgroundColor: color,
    borderRadius: 10,
    content: '" "',
    display: 'block',
    marginRight: 8,
    height: 10,
    width: 10
  }
})
const colourStyles: StylesConfig<OptionType> = {
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    const color = chroma(data.value)
    return {
      ...styles,
      backgroundColor:
        isDisabled ? undefined
        : isSelected ? data.value
        : isFocused ? color.alpha(0.1).css()
        : undefined,
      color:
        isDisabled ? '#ccc'
        : isSelected ?
          chroma.contrast(color, 'white') > 2 ?
            'white'
          : 'black'
        : data.value,
      cursor: isDisabled ? 'not-allowed' : 'default',

      ':active': {
        ...styles[':active'],
        backgroundColor:
          !isDisabled ?
            isSelected ? data.value
            : color.alpha(0.3).css()
          : undefined
      }
    }
  },
  input: (styles) => ({ ...styles, ...dot() }),
  singleValue: (styles, { data }) => ({ ...styles, ...dot(data.value) })
}
